import React from "react";
import PieChart from "components/charts/PieChart";
import Card from "components/card";
import { useUnidades } from "context/UnidadeContext";
import { useRelatorios } from "context/RelatorioContext";
import { useAprendizado } from "context/AprendizadoContext";
import { useDevices } from "context/DeviceContext";

interface Props {
  selectedUnidadeId: string | "Todas";
}

export default function RelatorioUnidades({ selectedUnidadeId }: Props) {
  const { unidades } = useUnidades();
  const { relatorios } = useRelatorios();
  const { aprendizadoData } = useAprendizado();
  const { devices } = useDevices(); 
  const hoje = new Date();
  const diaSemanaHoje = ["dom.", "seg.", "ter.", "qua.", "qui.", "sex.", "sab."][hoje.getDay()];
  const dataHojeStr = hoje.toISOString().split("T")[0];
  const diaSemanaDevice = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"][hoje.getDay()];
  const unidadesFiltradas = unidades.filter(u => 
    selectedUnidadeId === "Todas" || u.dispositivoId === selectedUnidadeId
  );

  const unidadesOperantesHoje = unidadesFiltradas.filter(unidade => {
    const device = devices.find(d => d.id === unidade.dispositivoId);
    return device && device.days.includes(diaSemanaDevice);
  });

  let unidadesDentroMeta = 0;
  let unidadesForaMeta = 0;

  unidadesOperantesHoje.forEach(unidade => {
    const aprendizado = aprendizadoData.find(a => a.dispositivoId === unidade.dispositivoId);
    if (!aprendizado) {
      unidadesForaMeta++;
      return;
    }

    const dataInicioAprendizado = new Date(aprendizado.dataCadastro);
    const dataFimAprendizado = new Date(dataInicioAprendizado);
    dataFimAprendizado.setDate(dataInicioAprendizado.getDate() + 28);
    const relatoriosAprendizado = relatorios.filter(r =>
      r.dispositivoId === unidade.dispositivoId &&
      r.DiaSemana === diaSemanaHoje &&
      new Date(r.Data) >= dataInicioAprendizado &&
      new Date(r.Data) <= dataFimAprendizado
    );

    const metaDiaria = relatoriosAprendizado.length > 0
      ? relatoriosAprendizado.reduce((sum, r) => sum + r.contador, 0) / relatoriosAprendizado.length
      : 0;

    if (metaDiaria === 0) {
      unidadesForaMeta++;
      return;
    }

    const contagemDoDia = relatorios
      .filter(r => r.dispositivoId === unidade.dispositivoId && r.Data === dataHojeStr)
      .reduce((sum, r) => sum + r.contador, 0);

    const progresso = (contagemDoDia / metaDiaria) * 100;

    if (progresso >= 90 && progresso < 120) {
      unidadesDentroMeta++;
    } else {
      unidadesForaMeta++;
    }
  });

  const totalUnidadesRelevantes = unidadesOperantesHoje.length;
  const percentualDentro = totalUnidadesRelevantes > 0 ? Math.round((unidadesDentroMeta / totalUnidadesRelevantes) * 100) : 0;
  const percentualFora = totalUnidadesRelevantes > 0 ? Math.round((unidadesForaMeta / totalUnidadesRelevantes) * 100) : 0;
  const percentualZero = totalUnidadesRelevantes === 0 ? Math.round(100) : 0;
  const pieChartData = [percentualDentro, percentualFora, percentualZero];
  const pieChartOptions = {
    labels: ["Dentro da Meta", "Fora da Meta"],
    colors: ["#B84343", "#934242"],
    chart: { type: "pie" },
    states: { hover: { filter: { type: "none" } } },
    legend: { show: false },
    dataLabels: { enabled: false },
    tooltip: { enabled: true, y: { formatter: (value: number) => `${value}%` } },
  };

  return (
    <Card extra="!p-[20px] text-center">
      <h4 className="text-lg font-bold text-navy-700 self-start">Unidades Hoje</h4>

      <div className="mb-auto flex h-[220px] w-full items-center justify-center">
        <PieChart chartOptions={pieChartOptions} chartData={pieChartData} />
      </div>

      <div className="flex flex-row !justify-between rounded-2xl px-6 py-3 shadow-2xl shadow-shadow-500">

        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-[#B84343]" />
            <p className="ml-1 text-sm font-normal text-gray-600">Dentro</p>
          </div>
          <p className="mt-px text-xl font-bold text-navy-700">
            {percentualDentro}%
          </p>
        </div>

        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-[#934242]" />
            <p className="ml-1 text-sm font-normal text-gray-600">Fora</p>
          </div>
          <p className="mt-px text-xl font-bold text-navy-700">
            {percentualFora}%
          </p>
        </div>

      </div>
    </Card>
  );
}
